import { type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface AnimatedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Max width class (e.g. "max-w-4xl", "max-w-md") */
  maxWidth?: string;
  /** Extra classes on the content panel */
  className?: string;
}

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

/** Opacity-only: scale/translate on this panel breaks nested wheel scrolling. */
const contentVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const springTransition = {
  duration: 0.28,
  ease: [0.16, 1, 0.3, 1] as const,
};

/**
 * Wraps Radix Dialog with framer-motion for smooth
 * enter and exit animations on both overlay and content.
 *
 * Centering uses a flex shell (not CSS translate on the panel) so
 * scrollable children keep reliable wheel behaviour.
 *
 * Usage:
 * ```tsx
 * <AnimatedDialog open={open} onOpenChange={setOpen} maxWidth="max-w-md">
 *   <Dialog.Title>...</Dialog.Title>
 *   ...content...
 * </AnimatedDialog>
 * ```
 */
export function AnimatedDialog({
  open,
  onOpenChange,
  children,
  maxWidth = "max-w-4xl",
  className,
}: AnimatedDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                transition={{ duration: 0.22 }}
              />
            </Dialog.Overlay>

            {/* Flex centering avoids transform on the scrollable dialog panel. */}
            <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
              <Dialog.Content
                asChild
                forceMount
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
              >
                <motion.div
                  data-animated-dialog-content=""
                  className={cn(
                    "pointer-events-auto w-[90vw] rounded-xl bg-white shadow-2xl",
                    maxWidth,
                    className
                  )}
                  variants={contentVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={springTransition}
                >
                  {children}
                </motion.div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
